import { useState, useEffect, useRef, useMemo } from "react";
import { useApi } from "@/hooks/useApi";
import { apiSend, apiUpload } from "@/lib/api";
import { toast } from "@/components/ui/feedback";

// 知识全库 —— 全局资料库（不绑定 scene）。
// 所有景点 QA 在召回时会同时拿这里的内容作为通识/兜底资料。

interface GlobalMaterial {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileUrl: string;
  parseStatus: "pending" | "parsing" | "parsed" | "failed";
  parseError: string;
  createdAt: string;
  updatedAt: string;
}

const TYPE_LABEL: Record<string, string> = {
  word: "Word",
  excel: "Excel",
  ppt: "PPT",
  pdf: "PDF",
  image: "图片",
  video: "视频",
};

const TYPE_BADGE: Record<string, string> = {
  word: "bg-blue-100 text-blue-700",
  excel: "bg-emerald-100 text-emerald-700",
  ppt: "bg-orange-100 text-orange-700",
  pdf: "bg-red-100 text-red-700",
  image: "bg-purple-100 text-purple-700",
  video: "bg-pink-100 text-pink-700",
};

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(iso: string): string {
  if (!iso) return "--";
  try {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  } catch { return iso; }
}

function StatusBadge({ s, err }: { s: string; err?: string }) {
  switch (s) {
    case "parsed":
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700"><i className="ri-check-line" />已解析</span>;
    case "parsing":
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"><i className="ri-loader-4-line animate-spin" />解析中</span>;
    case "failed":
      return <span title={err} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><i className="ri-close-line" />失败</span>;
    default:
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">待解析</span>;
  }
}

function KnowledgeLibraryPage() {
  const { data: items, reload, loading } = useApi<GlobalMaterial[]>("/global-materials", []);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 后台有 parsing 时轮询 — 解析完成后刷新状态
  useEffect(() => {
    const pending = items.some((m) => m.parseStatus === "pending" || m.parseStatus === "parsing");
    if (!pending) return;
    const t = setInterval(reload, 2500);
    return () => clearInterval(t);
  }, [items, reload]);

  const filtered = useMemo(
    () => items.filter((m) => search === "" || m.fileName.toLowerCase().includes(search.toLowerCase())),
    [items, search],
  );

  const toggleOne = (id: string) =>
    setSelected((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const toggleAll = () =>
    setSelected((p) => (p.size === filtered.length ? new Set() : new Set(filtered.map((m) => m.id))));

  const onPickFiles = () => fileInputRef.current?.click();

  // 简单串行上传：一次一个文件，避免单请求过大 / 后端 ingest 同时启太多 goroutine。
  const onUploadFiles = async (files: FileList | null) => {
    if (!files || !files.length) return;
    const list = Array.from(files);
    setUploading({ done: 0, total: list.length });
    let ok = 0, fail = 0;
    for (let i = 0; i < list.length; i++) {
      try {
        await apiUpload("/global-materials", list[i]);
        ok++;
      } catch {
        fail++;
      }
      setUploading({ done: i + 1, total: list.length });
    }
    setUploading(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast(`上传完成：成功 ${ok}${fail ? `，失败 ${fail}` : ""}`, fail ? "error" : "success");
    reload();
  };

  const onBatchDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`确定删除选中的 ${selected.size} 个文件？删除后无法恢复。`)) return;
    try {
      await apiSend("POST", "/global-materials/batch-delete", { ids: Array.from(selected) });
      toast(`已删除 ${selected.size} 个文件`, "success");
      setSelected(new Set());
      reload();
    } catch (e) {
      toast("删除失败：" + (e as Error).message, "error");
    }
  };

  const onDeleteOne = async (m: GlobalMaterial) => {
    if (!window.confirm(`确定删除「${m.fileName}」？`)) return;
    try {
      await apiSend("DELETE", `/global-materials/${m.id}`);
      toast("已删除", "success");
      reload();
    } catch (e) {
      toast("删除失败：" + (e as Error).message, "error");
    }
  };

  const onReparse = async (m: GlobalMaterial) => {
    try {
      await apiSend("POST", `/global-materials/${m.id}/reparse`);
      toast("已重新解析", "success");
      reload();
    } catch (e) {
      toast("重新解析失败：" + (e as Error).message, "error");
    }
  };

  const beginRename = (m: GlobalMaterial) => {
    setEditingId(m.id);
    setEditingName(m.fileName);
  };
  const commitRename = async (m: GlobalMaterial) => {
    const name = editingName.trim();
    if (!name || name === m.fileName) { setEditingId(null); return; }
    try {
      await apiSend("PUT", `/global-materials/${m.id}`, { fileName: name });
      toast("已重命名", "success");
      setEditingId(null);
      reload();
    } catch (e) {
      toast("重命名失败：" + (e as Error).message, "error");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">知识全库</h1>
          <p className="text-sm text-gray-500 mt-1">
            全局资料（不绑定景点）；所有问答都会从这里 + 当前景点资料联合召回。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => onUploadFiles(e.target.files)}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv"
          />
          <button
            onClick={onPickFiles}
            disabled={!!uploading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60 whitespace-nowrap"
          >
            <i className={uploading ? "ri-loader-4-line animate-spin mr-1.5" : "ri-upload-cloud-2-line mr-1.5"} />
            {uploading ? `上传中 ${uploading.done}/${uploading.total}` : "上传文件（可多选）"}
          </button>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "资料总数", value: `${items.length}`, cls: "border-gray-200 text-gray-900" },
          { label: "已解析", value: `${items.filter((m) => m.parseStatus === "parsed").length}`, cls: "border-emerald-200 text-emerald-700 bg-emerald-50" },
          { label: "解析中", value: `${items.filter((m) => m.parseStatus === "parsing" || m.parseStatus === "pending").length}`, cls: "border-amber-200 text-amber-700 bg-amber-50" },
          { label: "失败", value: `${items.filter((m) => m.parseStatus === "failed").length}`, cls: "border-red-200 text-red-700 bg-red-50" },
        ].map((c) => (
          <div key={c.label} className={`border rounded-lg p-4 ${c.cls}`}>
            <div className="text-xs text-gray-500 mb-1">{c.label}</div>
            <div className="text-2xl font-bold">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">共 {filtered.length} 个文件</span>
            {selected.size > 0 && (
              <button
                onClick={onBatchDelete}
                className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-md text-xs font-medium hover:bg-red-100"
              >
                <i className="ri-delete-bin-line mr-1" />删除所选（{selected.size}）
              </button>
            )}
          </div>
          <div className="relative w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索文件名"
              className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    className="rounded text-teal-600 focus:ring-teal-500"
                  />
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">文件名</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">大小</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">上传时间</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-400">
                  <i className="ri-loader-4-line animate-spin mr-1" />加载中…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                  暂无资料，点击右上角「上传文件」开始
                </td></tr>
              )}
              {filtered.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(m.id)}
                      onChange={() => toggleOne(m.id)}
                      className="rounded text-teal-600 focus:ring-teal-500"
                    />
                  </td>
                  <td className="px-3 py-2.5 max-w-md">
                    {editingId === m.id ? (
                      <input
                        value={editingName}
                        autoFocus
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={() => commitRename(m)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename(m);
                          else if (e.key === "Escape") setEditingId(null);
                        }}
                        className="w-full px-2 py-1 border border-teal-500 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                      />
                    ) : (
                      <div className="flex items-center gap-2 group">
                        {m.fileUrl ? (
                          <a href={m.fileUrl} target="_blank" rel="noreferrer" className="text-sm text-gray-900 hover:text-teal-600 truncate">{m.fileName}</a>
                        ) : (
                          <span className="text-sm text-gray-900 truncate">{m.fileName}</span>
                        )}
                        <button
                          onClick={() => beginRename(m)}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-teal-600 transition-opacity"
                          title="重命名"
                        >
                          <i className="ri-pencil-line" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TYPE_BADGE[m.fileType] || "bg-gray-100 text-gray-600"}`}>
                      {TYPE_LABEL[m.fileType] || m.fileType}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 whitespace-nowrap">{fmtSize(m.fileSize)}</td>
                  <td className="px-3 py-2.5">
                    <StatusBadge s={m.parseStatus} err={m.parseError} />
                    {m.parseStatus === "failed" && m.parseError && (
                      <div className="text-[11px] text-red-500 mt-0.5 max-w-xs truncate" title={m.parseError}>{m.parseError}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-500 whitespace-nowrap">{fmtDate(m.createdAt)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      {(m.parseStatus === "failed" || m.parseStatus === "parsed") && (
                        <button
                          onClick={() => onReparse(m)}
                          className="w-7 h-7 inline-flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded"
                          title="重新解析"
                        >
                          <i className="ri-refresh-line" />
                        </button>
                      )}
                      <button
                        onClick={() => onDeleteOne(m)}
                        className="w-7 h-7 inline-flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="删除"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default KnowledgeLibraryPage;
